import { useEffect, useCallback } from 'react';
import { 
  handleUserTyping, 
  handleNewMessage, 
  handleContentChunk, 
  handleStreamingComplete,
  handleDocumentStatus,
  handleTitleUpdated,
  handleMessageComplete,
  handleSearchComplete,
  handleToolCallStart,
  handleToolCallResult
} from '../utils/messageHandlers';
import websocketService from '../../../utils/websocketService';

export const useWebSocketConnection = ({ 
  chatId, 
  onChatCreated, 
  onTypingEvent,
  setMessages,
  setError,
  setIsLoading,
  sseStreamingActive,
  currentUser,
  toolCallManager
}) => {
  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'chat_update' && data.chatId === chatId) {
      const updateData = data.data;
      
      switch (updateData.type) {
        case 'user_typing':
          handleUserTyping(updateData, onTypingEvent);
          break;
          
        case 'new_message':
          handleNewMessage(updateData, setMessages, currentUser);
          // Don't set loading to false - let streaming system manage loading state
          break;
          
        case 'content_chunk':
          handleContentChunk(updateData, setMessages, setError, sseStreamingActive);
          break;
          
        case 'streaming_complete':
          handleStreamingComplete(setMessages, setError, setIsLoading, sseStreamingActive, chatId, updateData);
          break;
          
        case 'document_status':
          handleDocumentStatus(updateData, setMessages);
          break;

        case 'title_updated':
          handleTitleUpdated(data, updateData);
          break;

        case 'deep_research_progress':
          // Handle deep research progress updates via WebSocket
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            // Find the most recent bot message (placeholder or deep research message)
            const lastBotIndex = currentMessages.map((msg, index) => ({...msg, originalIndex: index}))
              .reverse()
              .find(msg => msg.sender === 'bot' && (msg.isTemporary === true || msg.isDeepResearch))?.originalIndex;
            
            if (lastBotIndex !== undefined) {
              const updatedMessages = [...currentMessages];
              updatedMessages[lastBotIndex] = {
                ...updatedMessages[lastBotIndex],
                isDeepResearch: true,
                metadata: {
                  ...updatedMessages[lastBotIndex].metadata,
                  stage: updateData.stage,
                  progress: updateData.progress
                }
                // Remove duplicate text - progress bar handles visual feedback
              };
              return updatedMessages;
            }
            return currentMessages;
          });
          break;

        case 'coreengine_routing':
          // Handle core engine routing updates silently
          break;
          
        case 'message_complete': {
          // Q3 DIAGNOSTIC: log the incoming messageId vs what's in React state
          const _incomingId = updateData.messageId;
          setMessages(prev => {
            const _ids = (Array.isArray(prev) ? prev : [])
              .filter(m => m.sender === 'bot')
              .map(m => ({ id: m._id, isStreaming: m.isStreaming, isComplete: m.isComplete, hasFile: !!m.generatedFile }));
            const _matched = _ids.find(m => m.id === _incomingId);
            console.log('[FILE-JOB WS] message_complete received', {
              incomingMessageId: _incomingId,
              hasGeneratedFile: !!updateData.generatedFile,
              jobId: updateData.jobId,
              completedToolId: updateData.completedToolId,
              botMessages: _ids,
              matchFound: !!_matched,
            });
            return prev; // no-op, just logging
          });
          handleMessageComplete(updateData, setMessages);
          // If this completion came from a background file job, also finalize the tool pill
          if (updateData.completedToolId && updateData.messageId) {
            setMessages(prev => {
              const msgs = Array.isArray(prev) ? prev : [];
              return msgs.map(m => {
                if (m._id !== updateData.messageId) return m;
                const positions = m.toolCallPositions || [];
                const updated = positions.map(p =>
                  p.toolId === updateData.completedToolId
                    ? { ...p, isComplete: true, status: updateData.completedToolStatus || 'Complete' }
                    : p
                );
                return { ...m, toolCallPositions: updated, toolCallsActive: updated.some(p => !p.isComplete) };
              });
            });
          }
          break;
        }

        case 'file_job_error':
          // Background file job failed — mark the tool pill as errored
          if (updateData.messageId) {
            setMessages(prev => {
              const msgs = Array.isArray(prev) ? prev : [];
              return msgs.map(m => {
                if (m._id !== updateData.messageId) return m;
                const positions = (m.toolCallPositions || []).map(p =>
                  p.toolId === updateData.completedToolId
                    ? { ...p, isComplete: true, status: 'Error creating file' }
                    : p
                );
                return {
                  ...m,
                  toolCallPositions: positions,
                  toolCallsActive: false,
                  isStreaming: false,
                  isComplete: true,
                };
              });
            });
          }
          break;
          
        case 'search_complete':
          handleSearchComplete(updateData, setMessages);
          break;

        case 'tool_call_start':
          handleToolCallStart(updateData, setMessages, toolCallManager);
          break;

        case 'tool_call_result':
          handleToolCallResult(updateData, setMessages, toolCallManager);
          break;

        case 'sent_draft_updated':
          if (updateData.messageId && updateData.metadata?.sentDrafts) {
            setMessages(prev => prev.map(m => m._id === updateData.messageId
              ? { ...m, metadata: { ...(m.metadata || {}), sentDrafts: updateData.metadata.sentDrafts } }
              : m
            ));
          }
          break;
          
        case 'thinking_start':
          // Skip WebSocket thinking_start when SSE is handling thinking
          break;
          
        case 'thinking_delta':
          // Skip WebSocket thinking deltas when SSE is handling thinking
          break;
          
        case 'thinking_complete':
          // Skip WebSocket thinking_complete when SSE is handling thinking
          break;
          
        case 'memory_update':
          console.log('🧠 WebSocket: Received memory_update event:', updateData);
          // Handle memory update - same as SSE memory_update
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            // Find the most recent bot message to attach memory indicator
            const lastBotMessage = [...currentMessages].reverse().find(msg => 
              msg.sender === 'bot' && (msg.isTemporary || msg.text === '[[LOADING_INDICATOR]]' || !msg.hasMemoryUpdate)
            );
            
            if (lastBotMessage) {
              console.log('🧠 WebSocket: Attaching memory indicator to message:', lastBotMessage._id);
              return currentMessages.map(msg => 
                msg._id === lastBotMessage._id ? {
                  ...msg,
                  hasMemoryUpdate: true,
                  memoryAction: updateData.memoryAction || 'remember',
                  memoryContent: updateData.memoryContent || 'Memory updated',
                  isTemporary: false // Ensure message is finalized
                } : msg
              );
            } else {
              console.warn('🧠 WebSocket: No suitable bot message found for memory indicator');
              return currentMessages;
            }
          });
          break;
          
        default:
          console.log('Unknown chat update type:', updateData.type);
      }
    }
  }, [chatId, onTypingEvent, setMessages, setError, setIsLoading, sseStreamingActive, currentUser]);

  // Subscribe to shared WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    websocketService.connect(token);

    const listenerId = chatId ? `chat-message-handler-${chatId}` : 'chat-message-handler';

    const sharedListener = (data) => {
      if (data.type === 'chat_update') {
        if (!chatId || data.chatId === chatId) {
          if (!chatId && data.chatId && onChatCreated && data.data?.type === 'new_message') {
            console.log('🆕 New chat detected, updating navigation:', data.chatId);
            onChatCreated(data.chatId);
          }
          handleWebSocketMessage(data);
        } else {
          // Process other chats so sidebar indicators stay fresh
          handleWebSocketMessage(data);
        }
        return;
      }

      handleWebSocketMessage(data);
    };

    websocketService.addListener(listenerId, sharedListener);

    return () => {
      websocketService.removeListener(listenerId);
    };
  }, [chatId, handleWebSocketMessage, onChatCreated]);

  // Health check for stuck messages
  useEffect(() => {
    const healthCheckInterval = setInterval(() => {
      if (document.hidden) return;
      // NEVER overwrite messages while SSE is actively streaming — prevents "Let me read the skill" etc. being replaced by error
      if (sseStreamingActive.current) return;

      setMessages(prev => {
        const currentMessages = Array.isArray(prev) ? prev : [];
        const now = Date.now();
        let hasStuckMessage = false;

        const updatedMessages = currentMessages.map(msg => {
          const elapsed = msg.timestamp ? (now - new Date(msg.timestamp).getTime()) : 0;
          const isToolActivity = msg.toolCallPositions?.length > 0 || msg.toolCallsActive || msg.hasToolCalls ||
            (msg.text && (
              (msg.text.includes('Loaded') && msg.text.includes('skill')) ||
              (msg.text.includes('read') && msg.text.includes('skill')) ||
              msg.text.includes('Building artifact') ||
              msg.text.includes('Executing') ||
              msg.text.includes('read_skill') ||
              msg.text.includes('create_html_artifact') ||
              msg.text.includes('generate_powerpoint')
            ));
          // Tool execution gets 2 min; other "stuck" states get 30 sec
          const threshold = isToolActivity ? 120000 : 30000;
          const isStuck = msg.sender === 'bot' &&
            (msg.isTemporary || msg.isThinking || msg.text === '[[LOADING_INDICATOR]]' || (msg.isStreaming && isToolActivity)) &&
            elapsed > threshold;

          if (!isStuck) return msg;

          if (msg.generatedImage && !msg.isStreaming) {
            console.log('Message has completed image generation, not marking as stuck:', msg._id);
            return msg;
          }
          if (msg.generatedFile) {
            console.log('Message has generated file, not marking as stuck:', msg._id);
            return msg;
          }
          if (msg.isAnalyzing || msg.analysisStatus) return msg;
          if (msg.isDeepResearch || msg.text?.includes('Deep Research') || msg.text?.includes('Analyzing')) return msg;
          if (msg.isGeneratingImage || msg.text?.includes('Creating image')) return msg;
          if (msg.isSearching || msg.hasWebSearch || msg.searchStatus) return msg;

          console.warn('Detected stuck message after', Math.round(elapsed / 1000), 's, providing fallback:', msg._id);
          hasStuckMessage = true;
          return {
            ...msg,
            text: "I apologize, but I'm having trouble generating a response. Please try refreshing the page and try again.",
            isTemporary: false,
            isThinking: false,
            isStreaming: false,
            isError: true
          };
        });
        
        if (hasStuckMessage && !sseStreamingActive.current) {
          setIsLoading(false);
        }
        
        return updatedMessages;
      });
    }, 30000);

    return () => clearInterval(healthCheckInterval);
  }, [setMessages, sseStreamingActive, setIsLoading]);

  return {
    handleWebSocketMessage
  };
}; 